import React, { useRef, useState } from 'react';
import { LabelledSelect } from '../../../../../../../components/LabelledSelect';
import { SelectOption } from '../../../../../../../components/Select';
import { EmailTemplate } from '../../../../../../../entities/EmailTemplate';
import { SendEmailTriggerRow } from './SendEmailTriggerRow';
import { PlanTemplate } from '../../../../../../../entities/applicant_tracking/PlanTemplate';
import { IconButton } from '../../../../../../../components/IconButton';
import { TriggerRowDropdown } from './Dropdown/TriggerRowDropdown';
import {
  SendEmailTrigger,
  Trigger,
} from '../../../../../../../entities/applicant_tracking/Trigger';
import { ApplicationFormTemplate } from '../../../../../../../entities/applicant_tracking/ApplicationFormTemplate';
import { AutoRejectTriggerRow } from './AutoRejectTriggerRow';
import { HrisProvider } from '../../../../../../../enums/IntegrationProvider/HrisProvider';
import { getHrisName } from '../../../../../../../utils/hris';
import { PushToHrisTriggerRow } from './PushToHrisTriggerRow';
import { SendSelfSchedulingLinkTriggerRow } from './SendSelfSchedulingLinkTriggerRow';
import { RejectReason } from '../../../../../../../entities/applicant_tracking/RejectReason';
import { DeleteTriggerConfirmationModal } from './DeleteTriggerConfirmationModal';

interface PropTypes {
  counter: number;
  planTemplate: PlanTemplate;
  emailTemplates: EmailTemplate[];
  applicationFormTemplate: ApplicationFormTemplate;
  trigger: Trigger;
  triggers: Trigger[];
  jobLocation: string;
  rejectReasons: RejectReason[];
  setTriggers: (triggers: Trigger[]) => void;
  onUpdateTrigger: (trigger: Trigger, index: number) => void;
  setDeleteModalOpen?: (isOpen: boolean) => void;
  disabled?: boolean;
  hrisProvider?: HrisProvider;
}

function showPushToHrisOption(
  selectedTriggers: Trigger[],
  currentTrigger: Trigger,
  hrisProvider?: HrisProvider,
): boolean {
  if (!hrisProvider) {
    return false;
  }

  const pushToHrisTrigger = selectedTriggers.find(
    (t) => t.type === 'push_to_hris',
  );

  // only show if it was not previously selected
  return !(!currentTrigger.id && pushToHrisTrigger);
}

function buildTriggerOptions(
  selectedTriggers: Trigger[],
  currentTrigger: Trigger,
  hrisProvider?: HrisProvider,
): SelectOption[] {
  const options = [
    {
      value: 'auto_reject',
      label: 'Auto-reject',
    },
    {
      value: 'send_email',
      label: 'Send Email',
    },
  ];

  if (showPushToHrisOption(selectedTriggers, currentTrigger, hrisProvider)) {
    options.splice(1, 0, {
      value: 'push_to_hris',
      label: `Export to ${getHrisName(hrisProvider)}`,
    });
  }

  options.push({
    value: 'send_self_scheduling_email',
    label: 'Send self-scheduling link',
  });

  return options;
}

function removeFromArray<T>(array: T[], index: number): T[] {
  const newArray = [...array];
  newArray.splice(index, 1);
  return newArray;
}

function mapToSelectOption(
  selectedTriggers: Trigger[],
  currentTrigger: Trigger,
  selectedValue?: string,
  hrisProvider?: HrisProvider,
): SelectOption[] {
  return buildTriggerOptions(
    selectedTriggers,
    currentTrigger,
    hrisProvider,
  ).map((v: SelectOption) => {
    return {
      value: v.value,
      label: v.label,
      selected: v.value === selectedValue,
    };
  });
}

export function TriggerRow(props: PropTypes) {
  const addtionalRowPortal = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState(
    mapToSelectOption(
      props.triggers,
      props.trigger,
      props.trigger.type,
      props.hrisProvider,
    ),
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const isSavedTrigger = !!props.trigger.id;

  const handleDeleteClick = () => {
    if (isSavedTrigger) {
      setIsDeleteModalOpen(true);
      props.setDeleteModalOpen?.(true);
    } else {
      props.setTriggers(removeFromArray(props.triggers, props.counter));
    }
  };

  const handleDeleteModalCancel = () => {
    setIsDeleteModalOpen(false);
    props.setDeleteModalOpen?.(false);
  };

  const handleDeleteModalContinue = () => {
    setIsDeleteModalOpen(false);
    props.setDeleteModalOpen?.(false);
    props.setTriggers(removeFromArray(props.triggers, props.counter));
  };

  return (
    <div className='mb-4 w-100'>
      <div className='d-flex flex-nowrap justify-content-start row gx-3'>
        <TriggerRowDropdown className={'col-3'}>
          <LabelledSelect
            options={options}
            selected={options.find((v) => v.selected)}
            labelClassName='fs-5 text-nowrap'
            label={`Automation ${props.counter + 1}`}
            placeholder='Select Automation'
            disabled={props.disabled || isSavedTrigger}
            onChange={(o) =>
              setOptions(
                mapToSelectOption(
                  props.triggers,
                  props.trigger,
                  o.value,
                  props.hrisProvider,
                ),
              )
            }
          />
        </TriggerRowDropdown>
        {options.find((value) => value.selected)?.value === 'auto_reject' && (
          <AutoRejectTriggerRow
            applicationFormTemplate={props.applicationFormTemplate}
            counter={props.counter}
            trigger={props.trigger}
            formData={props.triggers}
            jobLocation={props.jobLocation}
            emailTemplates={props.emailTemplates}
            addtionalRowPortal={addtionalRowPortal}
            onUpdateTrigger={props.onUpdateTrigger}
          />
        )}
        {options.find((value) => value.selected)?.value === 'push_to_hris' && (
          <PushToHrisTriggerRow
            trigger={props.trigger}
            counter={props.counter}
            onUpdateTrigger={props.onUpdateTrigger}
            hrisProvider={props.hrisProvider}
          />
        )}
        {options.find((value) => value.selected)?.value === 'send_email' && (
          <SendEmailTriggerRow
            emailTemplates={props.emailTemplates}
            rejectReasons={props.rejectReasons}
            planTemplate={props.planTemplate}
            counter={props.counter}
            trigger={props.trigger as SendEmailTrigger}
            formData={props.triggers}
            onUpdateTrigger={props.onUpdateTrigger}
          />
        )}
        {options.find((value) => value.selected)?.value ===
          'send_self_scheduling_email' && (
          <SendSelfSchedulingLinkTriggerRow
            emailTemplates={props.emailTemplates}
            planTemplate={props.planTemplate}
            counter={props.counter}
            trigger={props.trigger}
            formData={props.triggers}
            onUpdateTrigger={props.onUpdateTrigger}
          />
        )}
        {options.find((value) => value.selected) && (
          <div className='align-self-start col-md-auto'>
            <div className='mb-2 fs-5'>‎</div>
            <IconButton
              color='borderless text-primary'
              iconName={'bi-trash'}
              onClick={handleDeleteClick}
            />
          </div>
        )}
      </div>
      <div ref={addtionalRowPortal}></div>
      {isSavedTrigger && (
        <DeleteTriggerConfirmationModal
          triggerId={props.trigger.id}
          isOpen={isDeleteModalOpen}
          onCancel={handleDeleteModalCancel}
          onContinue={handleDeleteModalContinue}
        />
      )}
    </div>
  );
}
